import { Box, Typography } from "@mui/material";
import AdvisorAndUnifierTable from "./AdvisorAndUnifierTable";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useMemo } from "react";

const tableHeadings = {
  destination_field_name: "Metric",
  rule_description: "Rule",
  source_field_expression: "Affiliate Term",
  calculation_type: "Calculation Type",
  report_name: "Report Name",
  definition_json: "Definition JSON",
};

// Static Mtric column order (https://optival.atlassian.net/browse/GR-20948)
const METRIC_ORDER: Record<string, string[]> = {
  daily_user: [
    "activity_date",
    "user_unique_id",
    "registrations",
    "ftd",
    "qftd",
    "cpa_income",
    "net_revenue",
    "revshare_income",
    "total_income",
  ],
  daily_brand: [
    "activity_date",
    "registrations",
    "ftd",
    "qftd",
    "cpa_income",
    "net_revenue",
    "revshare_income",
    "total_income",
  ],
  monthly_brand: [
    "activity_date",
    "registrations",
    "ftd",
    "qftd",
    "cpa_income",
    "net_revenue",
    "revshare_income",
    "total_income",
  ],
};

const AdvisorAndUnifier = () => {
  const [searchParams] = useSearchParams();
  // Dynamically reads which tab is active from URL
  const reportType = searchParams.get("report") || "daily_user";

  // Unifier data
  const brandOverviewUnifierSettingsData = useSelector(
    (state: any) =>
      state.brandConfigurationReport.brandOverviewUnifierSettingsData[
        reportType
      ],
  );

  // Advisor data
  const brandOverviewAdvisorSettingsData = useSelector((state: any) => {
    const raw = state.brandConfigurationReport.brandOverviewAdvisorSettingsData;
    return Array.isArray(raw?.[reportType]) ? raw[reportType] : [];
  });

  // updated business logic For Rule column and static Metric column order (https://optival.atlassian.net/browse/GR-20948)
  const modifiedTableData = useMemo(() => {
    const unifierData = brandOverviewUnifierSettingsData || [];
    const advisorData = brandOverviewAdvisorSettingsData || [];
    const UNIFIER_RULE = "Value is updated from Unifier";
    const NOT_MAPPED = "This Field is not mapped for this Report";
    const matchedAdvisorKeys = new Set<string>();

    const mergedRows = unifierData.map((row: any) => {
      const advisorItem = advisorData.find(
        (item: any) => item?.field_map === row?.destination_field_name,
      );
      if (advisorItem?.field_map) matchedAdvisorKeys.add(advisorItem.field_map);

      const rule_description = advisorItem
        ? (advisorItem.rule_description ?? "")
        : UNIFIER_RULE;

      return { ...row, rule_description, isAdvisorOnly: false };
    });

    // rule_description === "Value is updated from Unifier" ? "This Field is not mapped for this Report" ? advisorItem?.rule_description ?? ""
    const advisorOnlyRows = advisorData
      .filter((item: any) => !matchedAdvisorKeys.has(item?.field_map))
      .map((item: any) => ({
        source_field_expression: "",
        destination_field_name: item?.field_map ?? "",
        calculation_type: "",
        report_name: "",
        rule_description:
          item?.rule_description === UNIFIER_RULE
            ? NOT_MAPPED
            : (item?.rule_description ?? ""),
        isAdvisorOnly: true,
      }));

    const combined = [...mergedRows, ...advisorOnlyRows];
    const fixedOrder = METRIC_ORDER[reportType] ?? [];

    if (fixedOrder.length === 0) return combined;

    const fixedOrderLower = fixedOrder.map((k) => k.toLowerCase());

    const pinnedRows = fixedOrderLower
      .map((key) =>
        combined.find(
          (row) => row.destination_field_name?.toLowerCase() === key,
        ),
      )
      .filter(Boolean);

    const pinnedKeys = new Set(fixedOrderLower);
    const remainingRows = combined.filter(
      (row) => !pinnedKeys.has(row.destination_field_name?.toLowerCase()),
    );

    return [...pinnedRows, ...remainingRows];
  }, [
    brandOverviewUnifierSettingsData,
    brandOverviewAdvisorSettingsData,
    reportType,
  ]);

  return (
    <Box px={2} py={1}>
      <Typography variant="h6" gutterBottom>
        Advisor & Unifier Settings
      </Typography>
      <Typography variant="body1" color="textSecondary">
        Clicking on a metric name will open a popup that shows raw data from the
        corresponding raw_data_table, resolved by the raw_table _id of the row
        and filtered by the selected dates.
      </Typography>
      <AdvisorAndUnifierTable
        tableHeadings={tableHeadings}
        modifiedTableData={modifiedTableData}
        reportType={reportType}
      />
    </Box>
  );
};

export default AdvisorAndUnifier;
